/**
 * Ofertas — índice global (Paso 6 spec v3.2.A).
 *
 * Listado global de todas las ofertas del comercial con filtros + KPIs pipeline.
 * Endpoint: GET /api/ofertas (ya filtra por responsable_comercial).
 *
 * DEC-006: versiones viven DENTRO de la oferta. La tabla muestra la versión activa.
 * Para ver el histórico, abrir la oferta → Constructor → Historial.
 */
import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowLeft, Loader2, Search, Package, MessageSquare, Plus, X,
  PlayCircle, CheckCircle2, XCircle, FileEdit, TrendingUp, Wallet,
} from "lucide-react";
import ConstructorOferta from "./ConstructorOferta";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const authAxios = axios.create();
authAxios.interceptors.request.use((cfg) => {
  const token = localStorage.getItem("mobilsafe_token");
  if (token) cfg.headers.Authorization = `Bearer ${token}`;
  return cfg;
});

const TABS = [
  { v: "", label: "Todas" },
  { v: "borrador", label: "Borrador" },
  { v: "activa", label: "Activas" },
  { v: "ganada", label: "Ganadas" },
  { v: "perdida", label: "Perdidas" },
];

export default function Ofertas({ onBack }) {
  const [ofertas, setOfertas] = useState([]);
  const [clientes, setClientes] = useState({});
  const [loading, setLoading] = useState(true);
  const [filtroEstado, setFiltroEstado] = useState("");
  const [q, setQ] = useState("");
  const [ofertaAbierta, setOfertaAbierta] = useState(null);
  const [mostrarModalNueva, setMostrarModalNueva] = useState(false);
  const [creando, setCreando] = useState(false);

  useEffect(() => { cargar(); }, []);

  async function cargar() {
    setLoading(true);
    try {
      const o = await authAxios.get(`${API}/ofertas`);
      setOfertas(o.data || []);
    } catch (e) {
      console.error("GET /api/ofertas falló:", e?.response?.status, e?.message);
      toast.error("Error cargando ofertas");
    }
    try {
      const c = await authAxios.get(`${API}/clientes`);
      const mapa = {};
      (c.data || []).forEach((cl) => { mapa[cl.id] = cl; });
      setClientes(mapa);
    } catch (e) {
      console.error("GET /api/clientes falló:", e?.response?.status, e?.message);
    }
    setLoading(false);
  }

  const kpis = useMemo(() => {
    const total = ofertas.length;
    const activas = ofertas.filter((o) => o.estado === "activa").length;
    const ganadas = ofertas.filter((o) => o.estado === "ganada").length;
    const perdidas = ofertas.filter((o) => o.estado === "perdida").length;
    const pipeline = ofertas
      .filter((o) => o.estado === "activa" || o.estado === "borrador")
      .reduce((s, o) => s + (o.importe_actual || 0), 0);
    const ratio = (ganadas + perdidas) > 0
      ? Math.round((ganadas / (ganadas + perdidas)) * 100)
      : 0;
    return { total, activas, ganadas, perdidas, pipeline, ratio };
  }, [ofertas]);

  const filtradas = useMemo(() => {
    let arr = [...ofertas];
    if (filtroEstado) arr = arr.filter((o) => o.estado === filtroEstado);
    if (q.trim()) {
      const s = q.toLowerCase();
      arr = arr.filter((o) => {
        const cl = clientes[o.cliente_id];
        return (cl?.empresa || "").toLowerCase().includes(s)
          || (cl?.cif || "").toLowerCase().includes(s);
      });
    }
    return arr;
  }, [ofertas, filtroEstado, q, clientes]);

  if (ofertaAbierta) {
    const cl = clientes[ofertaAbierta.cliente_id];
    return <ConstructorOferta ofertaId={ofertaAbierta.id} cliente={cl}
      onBack={() => { setOfertaAbierta(null); cargar(); }} />;
  }

  // DEC-004 Camino B · crea oferta directa (sin reunión previa)
  async function crearOfertaDirecta(cliente_id) {
    if (creando) return;
    setCreando(true);
    try {
      const res = await authAxios.post(`${API}/ofertas`, { cliente_id, origen: "directa" });
      toast.success("Oferta creada");
      setMostrarModalNueva(false);
      setOfertaAbierta(res.data);
    } catch (e) {
      toast.error("Error creando oferta: " + (e?.response?.data?.detail || e.message || ""));
    }
    setCreando(false);
  }

  // DEC-023 · oferta sin cliente real (cuelga del cliente borrador del comercial)
  async function crearOfertaBorrador(nombre) {
    if (creando || !nombre.trim()) return;
    setCreando(true);
    try {
      const res = await authAxios.post(`${API}/ofertas/borrador`, { nombre: nombre.trim() });
      toast.success("Borrador creado");
      setMostrarModalNueva(false);
      cargar();
      setOfertaAbierta(res.data);
    } catch (e) {
      toast.error("Error creando borrador: " + (e?.response?.data?.detail || e.message || ""));
    }
    setCreando(false);
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="ofertas-page">
      <header className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center gap-3">
          <button onClick={onBack} data-testid="ofertas-back"
            className="p-2 hover:bg-slate-100 rounded-lg">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex-1">
            <h1 className="text-xl font-bold flex items-center gap-2">
              <Package className="w-5 h-5 text-emerald-600" /> Ofertas
            </h1>
            <p className="text-xs text-slate-500">Índice global · Paso 6 spec v3.2.A · DEC-006 versiones anidadas</p>
          </div>
          <button
            onClick={() => setMostrarModalNueva(true)}
            data-testid="ofertas-nueva-btn"
            className="px-4 py-2 bg-emerald-600 text-white rounded-lg text-sm font-semibold hover:bg-emerald-700 inline-flex items-center gap-2"
            title="Crear oferta directa sin reunión previa (DEC-004 Camino B)">
            <Plus className="w-4 h-4" /> Nueva oferta
          </button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-6 space-y-5">
        {/* KPIs pipeline */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <Kpi testid="kpi-total" icon={<Package className="w-4 h-4" />} label="Total" value={kpis.total} />
          <Kpi testid="kpi-activas" icon={<PlayCircle className="w-4 h-4 text-blue-600" />} label="Activas" value={kpis.activas} />
          <Kpi testid="kpi-ganadas" icon={<CheckCircle2 className="w-4 h-4 text-emerald-600" />} label="Ganadas" value={kpis.ganadas} />
          <Kpi testid="kpi-ratio" icon={<TrendingUp className="w-4 h-4 text-violet-600" />} label="Ratio cierre" value={`${kpis.ratio}%`} />
          <Kpi testid="kpi-pipeline" icon={<Wallet className="w-4 h-4 text-amber-600" />} label="Pipeline" value={`${kpis.pipeline.toLocaleString("es-ES")} €`} />
        </div>

        {/* Tabs estado + búsqueda */}
        <div className="bg-white rounded-xl border p-3 flex items-center gap-2 flex-wrap">
          <div className="flex items-center gap-2 flex-1 min-w-[200px]">
            <Search className="w-4 h-4 text-slate-400" />
            <input type="text" value={q} onChange={(e) => setQ(e.target.value)}
              data-testid="ofertas-buscar"
              placeholder="Buscar por cliente o CIF…"
              className="flex-1 px-2 py-1.5 text-sm focus:outline-none" />
          </div>
          <div className="flex items-center gap-1 flex-wrap">
            {TABS.map((t) => (
              <button key={t.v} onClick={() => setFiltroEstado(t.v)}
                data-testid={`tab-estado-${t.v || "todas"}`}
                className={`px-3 py-1.5 text-xs rounded-md border transition ${
                  filtroEstado === t.v
                    ? "bg-emerald-600 text-white border-emerald-600"
                    : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"
                }`}>
                {t.label}
              </button>
            ))}
          </div>
        </div>

        {/* Tabla */}
        <div className="bg-white rounded-xl border overflow-hidden">
          {loading ? (
            <div className="p-10 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
          ) : filtradas.length === 0 ? (
            <div className="p-10 text-center text-slate-400 text-sm">
              {ofertas.length === 0
                ? "Sin ofertas todavía. Crea una desde la ficha del cliente o desde el informe consultivo."
                : "Sin resultados para este filtro."}
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="text-left p-3">Cliente</th>
                  <th className="text-left p-3">Origen</th>
                  <th className="text-center p-3">Versión activa</th>
                  <th className="text-right p-3">Importe</th>
                  <th className="text-center p-3">Estado</th>
                  <th className="text-left p-3">Actualizado</th>
                  <th className="text-right p-3"></th>
                </tr>
              </thead>
              <tbody>
                {filtradas.map((o) => {
                  const cl = clientes[o.cliente_id];
                  const vActiva = (o.versiones || []).find((v) => v.version === o.version_activa);
                  const tieneFeedback = (vActiva?.feedback_cliente || []).length > 0;
                  const numVersiones = (o.versiones || []).length;
                  return (
                    <tr key={o.id} className="border-t hover:bg-slate-50" data-testid={`oferta-row-${o.id}`}>
                      <td className="p-3">
                        {o.nombre && (
                          <div className="font-semibold text-emerald-700 text-sm">{o.nombre}</div>
                        )}
                        <div className={`${o.nombre ? "text-xs text-slate-500" : "font-semibold"}`}>
                          {cl?.empresa || <span className="text-slate-400 italic">cliente no disponible</span>}
                          {cl?.es_borrador && (
                            <span className="ml-2 inline-flex items-center text-[10px] px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded-full font-bold uppercase tracking-wide" data-testid={`badge-borrador-${o.id}`}>
                              Borrador
                            </span>
                          )}
                        </div>
                        {cl?.cif && <div className="text-xs text-slate-500">{cl.cif}</div>}
                      </td>
                      <td className="p-3">
                        <OrigenBadge origen={o.origen} />
                      </td>
                      <td className="p-3 text-center">
                        <span className="font-bold text-slate-800">v{o.version_activa}</span>
                        <span className="text-xs text-slate-400"> / {numVersiones}</span>
                        {tieneFeedback && (
                          <div className="mt-0.5" data-testid={`badge-feedback-${o.id}`}>
                            <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 bg-violet-100 text-violet-700 rounded-full font-medium">
                              <MessageSquare className="w-3 h-3" /> Con feedback
                            </span>
                          </div>
                        )}
                      </td>
                      <td className="p-3 text-right font-semibold text-slate-800">
                        {(o.importe_actual || 0).toLocaleString("es-ES")} €
                      </td>
                      <td className="p-3 text-center"><EstadoBadge estado={o.estado} /></td>
                      <td className="p-3 text-slate-600 text-xs">
                        {new Date(o.updated_at).toLocaleDateString("es-ES")}
                      </td>
                      <td className="p-3 text-right">
                        <button onClick={() => setOfertaAbierta(o)}
                          data-testid={`btn-abrir-oferta-${o.id}`}
                          className="text-xs px-2.5 py-1 bg-emerald-600 text-white rounded hover:bg-emerald-700 inline-flex items-center gap-1">
                          <FileEdit className="w-3 h-3" /> Abrir
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </main>

      {mostrarModalNueva && (
        <ModalNuevaOferta
          clientes={Object.values(clientes)}
          creando={creando}
          onCerrar={() => setMostrarModalNueva(false)}
          onSeleccionar={crearOfertaDirecta}
          onBorrador={crearOfertaBorrador}
        />
      )}
    </div>
  );
}

function ModalNuevaOferta({ clientes, creando, onCerrar, onSeleccionar, onBorrador }) {
  const [qCliente, setQCliente] = useState("");
  const [modoBorrador, setModoBorrador] = useState(false);
  const [nombreBorrador, setNombreBorrador] = useState("");
  // Excluir clientes borrador del listado (DEC-023 · sólo se eligen clientes reales)
  const clientesReales = useMemo(() => clientes.filter((c) => !c.es_borrador), [clientes]);
  const filtrados = useMemo(() => {
    if (!qCliente.trim()) return clientesReales;
    const s = qCliente.toLowerCase();
    return clientesReales.filter(
      (c) => (c.empresa || "").toLowerCase().includes(s) || (c.cif || "").toLowerCase().includes(s)
    );
  }, [clientesReales, qCliente]);

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="modal-nueva-oferta"
      onClick={onCerrar}>
      <div className="bg-white rounded-xl w-full max-w-lg shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b">
          <div>
            <h2 className="text-base font-bold">{modoBorrador ? "Oferta sin cliente · borrador" : "Nueva oferta directa"}</h2>
            <p className="text-xs text-slate-500">
              {modoBorrador
                ? "Pónle un nombre para reconocerla. Asignarás el cliente cuando lo tengas (DEC-023)."
                : "Selecciona un cliente · DEC-004 Camino B"}
            </p>
          </div>
          <button onClick={onCerrar} data-testid="modal-nueva-cerrar"
            className="p-1.5 hover:bg-slate-100 rounded">
            <X className="w-4 h-4" />
          </button>
        </div>

        {!modoBorrador ? (
          <div className="p-5 space-y-3">
            <div className="flex items-center gap-2 border rounded-lg px-2">
              <Search className="w-4 h-4 text-slate-400" />
              <input type="text" value={qCliente} onChange={(e) => setQCliente(e.target.value)}
                data-testid="modal-nueva-buscar"
                placeholder="Buscar cliente por empresa o CIF…"
                className="flex-1 px-2 py-2 text-sm focus:outline-none" />
            </div>

            <div className="max-h-60 overflow-y-auto border rounded-lg divide-y" data-testid="modal-nueva-lista">
              {clientesReales.length === 0 ? (
                <div className="p-6 text-center text-sm text-slate-400">
                  Aún no tienes clientes. Puedes crear un borrador sin cliente más abajo.
                </div>
              ) : filtrados.length === 0 ? (
                <div className="p-6 text-center text-sm text-slate-400">Sin resultados</div>
              ) : (
                filtrados.map((c) => (
                  <button key={c.id}
                    onClick={() => onSeleccionar(c.id)}
                    disabled={creando}
                    data-testid={`modal-nueva-cliente-${c.id}`}
                    className="w-full px-4 py-3 text-left hover:bg-emerald-50 disabled:opacity-50 flex items-center justify-between">
                    <div>
                      <div className="font-semibold text-sm">{c.empresa || "(sin nombre)"}</div>
                      <div className="text-xs text-slate-500">{c.cif || "—"}</div>
                    </div>
                    {creando ? <Loader2 className="w-4 h-4 animate-spin text-emerald-600" /> : (
                      <span className="text-xs text-emerald-700 font-semibold">Crear →</span>
                    )}
                  </button>
                ))
              )}
            </div>

            <button
              onClick={() => setModoBorrador(true)}
              data-testid="modal-nueva-borrador-link"
              className="w-full text-center text-xs text-slate-500 hover:text-emerald-700 underline underline-offset-2 py-1">
              → Crear sin cliente · le pondré nombre y la asignaré después
            </button>
          </div>
        ) : (
          <div className="p-5 space-y-3" data-testid="modal-nueva-borrador-form">
            <label className="block">
              <span className="text-xs font-semibold text-slate-700">Nombre de la oferta borrador</span>
              <input
                type="text"
                autoFocus
                value={nombreBorrador}
                onChange={(e) => setNombreBorrador(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" && nombreBorrador.trim()) onBorrador(nombreBorrador); }}
                data-testid="modal-nueva-borrador-input"
                placeholder='Ej. "Distribuidora ABC · exploración mayo"'
                className="w-full mt-1 px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-emerald-500"
              />
              <span className="text-[10px] text-slate-400 block mt-1">
                Cuelga del cliente <code>Borrador · {/* email del comercial */}</code>. Podrás asignarle un cliente real desde el constructor.
              </span>
            </label>
            <div className="flex justify-between gap-2 pt-2">
              <button
                onClick={() => setModoBorrador(false)}
                data-testid="modal-nueva-borrador-volver"
                className="text-xs px-3 py-1.5 text-slate-600 hover:text-slate-800">
                ← Volver al listado
              </button>
              <button
                onClick={() => onBorrador(nombreBorrador)}
                disabled={creando || !nombreBorrador.trim()}
                data-testid="modal-nueva-borrador-crear"
                className="px-4 py-2 text-sm bg-emerald-600 text-white rounded-lg font-semibold hover:bg-emerald-700 disabled:opacity-50 inline-flex items-center gap-2">
                {creando && <Loader2 className="w-4 h-4 animate-spin" />}
                Crear borrador
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Kpi({ icon, label, value, testid }) {
  return (
    <div className="bg-white rounded-xl border p-3" data-testid={testid}>
      <div className="flex items-center gap-2 text-xs text-slate-500 mb-1">{icon} {label}</div>
      <div className="text-2xl font-bold text-slate-900">{value}</div>
    </div>
  );
}

function EstadoBadge({ estado }) {
  const config = {
    borrador: { bg: "bg-slate-100", text: "text-slate-600", label: "Borrador", Icon: FileEdit },
    activa:   { bg: "bg-blue-100",  text: "text-blue-700",  label: "Activa",   Icon: PlayCircle },
    ganada:   { bg: "bg-emerald-100", text: "text-emerald-700", label: "Ganada", Icon: CheckCircle2 },
    perdida:  { bg: "bg-rose-100",  text: "text-rose-700",  label: "Perdida",  Icon: XCircle },
  }[estado] || { bg: "bg-slate-100", text: "text-slate-600", label: estado, Icon: FileEdit };
  const Icon = config.Icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${config.bg} ${config.text}`}>
      <Icon className="w-3 h-3" /> {config.label}
    </span>
  );
}

function OrigenBadge({ origen }) {
  if (origen === "reunion") {
    return <span className="text-xs px-2 py-0.5 bg-violet-50 text-violet-700 rounded font-medium">Desde reunión</span>;
  }
  return <span className="text-xs px-2 py-0.5 bg-slate-50 text-slate-600 rounded">Directa</span>;
}
